import FeatureModule from './module'
import {parseQueryParams} from './../../utils/url-helpers'
import {FEATURE_NAMESPACE, SWITCH, SWITCH_VALUE} from './module/types'
import {AUTH_NAMESPACE} from './../auth/module/types'
import {ID_NAMESPACE, SET_PROFILE} from './../auth/id/module/types'
import FeatureList from './feature-list'
import {fetch} from 'whatwg-fetch'
import {ID} from '../../enums/clients'

import Environment from './../environment'

let store
let featureQueryString

let idFeatureFlagPrefix = 'switch:exchange.smarttech.com/'
let queryStringDelimiter = '~'

/**
 * Implements a vue plugin and vuex plugin for controlling feature flags
 */
let Feature = {
  /**
   * install entry-point function for a Vue plugin
   */
  install(Vue) {
    Vue.prototype.$feature = (s) => {
      /**
       * If the feature flag does not exist, return false so that negative checks
       * don't fail. Prevents bugs arising from server side flags unknown to the
       * client, or other flags not yet listed in feature-list.js.
       */
      if (!store.state[FEATURE_NAMESPACE].hasOwnProperty(s)) {
        return false
      }

      return store.state[FEATURE_NAMESPACE][s]
    }
  },

  load() {
    // Sometimes we need feature switches early on during initialization or before signing in.
    //
    // For prod, these must be enabled on ID. Query string features will be rejected until the
    // user is signed in with a SMART email (which is too late).
    //
    // For non-prod environments, these may also be enabled on ID, but it will be applied globally
    // for every dev/tester. Since we don't need email validation on non-prod, we can just take
    // the feature switches in the query string and apply them immediately.
    if (!Environment.isProd() && !Environment.isPreprod()) {
      const queryObj = parseQueryParams(window.location.search)
      this.setFeatures(this.queryStringToFeatures(queryObj.feature))
    }

    return this.readFeatureFlagsFromServer()
  },

  /**
   * plugin entry-point function for a Vuex plugin
   */
  plugin(newStore) {
    store = newStore

    if (!store.state[FEATURE_NAMESPACE]) {
      store.registerModule(FEATURE_NAMESPACE, FeatureModule)
    }

    // Install window.setFeature for debugging
    //
    // Leaving this in all environments for now.  Might want it on prod for
    // debugging, but if not, wrap around in: process.env.NODE_ENV !== 'production'
    window.setFeature = Feature.setFeature.bind(Feature)
    window.setFeatureValue = Feature.setFeatureValue.bind(Feature)
    window.setFeatureQueryObject = Feature.processQueryObject.bind(Feature)

    // Use mutations to process the query string parameters
    // This is required now that flags also depends on state.auth.id.email
    store.subscribe((mutation, state) => {
      let query = parseQueryParams(window.location.search)
      switch (mutation.type) {
        case 'route/ROUTE_CHANGED':
        case `${AUTH_NAMESPACE}/${ID_NAMESPACE}/${SET_PROFILE}`:
          Feature.processQueryObject(query)
          break
      }
    })

    let i, key

    // Init Features in SessionStorage
    for (i = 0; i < window.sessionStorage.length; i++) {
      const VALUE = '-value'
      key = window.sessionStorage.key(i)
      if (!key.includes('uk-img')) {
        // variable is a boolean
        if (key.indexOf(VALUE) > -1) {
          let value = window.sessionStorage.getItem(key)
          key = key.replace(VALUE, '')
          store.commit(FEATURE_NAMESPACE + '/' + SWITCH_VALUE, {[key]: value})
        }

        store.commit(FEATURE_NAMESPACE + '/' + SWITCH, {[key]: true})
      }
    }

    // Init Known Features not in ID
    for (i = 0; i < FeatureList.length; i++) {
      key = FeatureList[i].name
      if (!store.state[FEATURE_NAMESPACE].hasOwnProperty(key)) {
        store.commit(FEATURE_NAMESPACE + '/' + SWITCH, {[key]: false})
      }
    }
  },

  /**
   * Dispatch a Vuex action to set flags
   * @param {object} features - map of feature key value pairs, key is flag
   *                            name, and value is true|false
   */
  setFeatures(features) {
    store.dispatch(FEATURE_NAMESPACE + '/' + SWITCH, features)
  },

  /**
   * Shortcut function to set a single flag
   * @param {string} name - name of feature flag
   * @param {bool} state - true or false whether the flag is set or not
   */
  setFeature(name, state) {
    this.setFeatures({[name]: state})
  },

  /**
   * Dispatch a Vuex action to set flag value
   * @param {object} features - map of feature key value pairs, key is flag
   *                            name, and value is true|false
   */
  setFeatureValue(name, value) {
    store.dispatch(FEATURE_NAMESPACE + '/' + SWITCH_VALUE, {[name]: value})
  },

  /**
   * Processes a query object from vue-router (this.$router) and sets feature
   * flags based on input.
   *
   * In production and preprod environments, do not process query string parameters unless
   * you are signed in with a @smartwizardschool.com address.
   *
   * @param {object} queryObject - should contain key value pairs of query strings
   *                               We're interested in 'feature', i.e. ?feature=<str>
   */
  processQueryObject(queryObject) {
    let isProd = Environment.isProd()
    let isPreProd = Environment.isPreprod()
    let emailAddress = store.state[AUTH_NAMESPACE].id.email.toLowerCase()
    let hasSmartWizardSchoolAddress = emailAddress.includes('@smartwizardschool.com') || emailAddress.includes('@smarttech.com')

    if (isProd && isPreProd && !hasSmartWizardSchoolAddress) {
      return
    }

    this.setFeatures(this.queryStringToFeatures(queryObject.feature))
  },

  /**
   * Generate a query string value for ?feature= based on the current flags in
   * the Vuex Store
   */
  featuresToQuery() {
    return this.featuresToQueryString(store.state[FEATURE_NAMESPACE])
  },

  /**
   * Process a string, usually from the query string, and returns a map of features
   * @param {string} featureStr - comma delimited string, where values from strings
   *                              are features that should be on (true).  If value
   *                              ends in '~off', it will instead be set to false
   * @return {object} feature map - 'a,b,c~off' => { a: true, b: true, c: false }
   *                              - an empty map if featureStr cannot be processed
   */
  queryStringToFeatures(featureStr) {
    if (!featureStr) {
      return {}
    }

    featureQueryString = featureStr

    let featureList = featureStr.split(/[ ,]+/)
    if (featureList.length < 1) {
      return {}
    }

    let features = {}
    featureList.forEach((f) => {
      let tokens = f.split(queryStringDelimiter)
      features[tokens[0]] = !(tokens.length > 1 && tokens[1] === 'off')
    })

    return features
  },

  /**
   * Process a feature map, and return a query string value that represents that map
   * @param {object} feature map - { a: true, b: true, c: false } => 'a,b,c~off'
   * @return {string} featureStr
   */
  featuresToQueryString(featureMap) {
    let qs = ''

    Object.keys(featureMap).forEach((feature) => {
      let off = !featureMap[feature].enabled ? queryStringDelimiter + 'off' : ''
      qs += `${feature}${off},`
    })
    // Trim off last ,
    qs = qs.substr(0, qs.length - 1)

    return qs
  },

  /**
   * Read feature flags from server
   */
  readFeatureFlagsFromServer() {
    return new Promise((resolve, reject) => {
      let url = `${Environment.serviceUrlFor(ID)}/api/feature/switch`

      fetch(url).then((response) => {
        return response.text()
      }).then((json) => {
        let idFlags = JSON.parse(json)
        // Set flags from ID
        this.setFeatures(this.idFlagsToFeatureMap(idFlags))

        // Then, set features specified by query string, so it overrides server flags
        this.setFeatures(this.queryStringToFeatures(featureQueryString))

        resolve()
      }).catch(err => {
        reject(new Error(err))
      })
    })
  },

  /**
   * Process Flags from ID feature flag format to our feature map format
   */
  idFlagsToFeatureMap(idFlags) {
    let serverFlags = {}

    idFlags.filter((flag) => {
      return flag.name.indexOf(idFeatureFlagPrefix) >= 0
    }).forEach((flag) => {
      // Shorten the flags so we don't have switch:suite.smarttech.com/ everywhere
      let shortFlag = flag.name.substr(idFeatureFlagPrefix.length, flag.length)
      serverFlags[shortFlag] = flag.enabled
    })

    return serverFlags
  }
}

export default Feature
